import React from "react";
import { Books, Buildings, ChartBar, FunnelSimple } from "@phosphor-icons/react";

function formatPercentile(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2) : "0.00";
}

type ShortlistCollegesEmptyProps = { onBrowse: () => void };

export function ShortlistCollegesEmpty({ onBrowse }: ShortlistCollegesEmptyProps) {
  return (
    <div className="rr-empty-panel" role="status">
      <div className="rr-empty-panel-icon rr-empty-panel-icon--accent" aria-hidden>
        <Buildings size={30} weight="duotone" />
      </div>
      <h3 className="rr-empty-panel-title">No colleges in your shortlist</h3>
      <p className="rr-empty-panel-text">
        Add colleges you care about from the directory. This view shows how your admission chances line up for each one using your exam score, percentile, and category.
      </p>
      <button type="button" className="rr-empty-panel-cta" onClick={onBrowse}>
        Browse &amp; shortlist colleges
      </button>
    </div>
  );
}

type ShortlistCoursesEmptyProps = { onBrowse: () => void };

export function ShortlistCoursesEmpty({ onBrowse }: ShortlistCoursesEmptyProps) {
  return (
    <div className="rr-empty-panel" role="status">
      <div className="rr-empty-panel-icon rr-empty-panel-icon--accent" aria-hidden>
        <Books size={30} weight="duotone" />
      </div>
      <h3 className="rr-empty-panel-title">No courses in your shortlist</h3>
      <p className="rr-empty-panel-text">
        Shortlist programs you are considering. We will plot your likelihoods across your picks for the category and score you are analysing.
      </p>
      <button type="button" className="rr-empty-panel-cta" onClick={onBrowse}>
        Browse &amp; shortlist courses
      </button>
    </div>
  );
}

type HapEmptyNoDataProps = {
  examLabel?: string;
  percentile: number;
  category: string;
};

export function HapEmptyNoData({ examLabel, percentile, category }: HapEmptyNoDataProps) {
  const exam = examLabel?.trim() || "this exam";
  const cat = category?.trim() || "your category";

  return (
    <div className="rr-empty-panel rr-empty-panel--embed" role="status">
      <div className="rr-empty-panel-icon rr-empty-panel-icon--muted" aria-hidden>
        <ChartBar size={28} weight="duotone" />
      </div>
      <h3 className="rr-empty-panel-title">No data for this combination</h3>
      <p className="rr-empty-panel-text">
        We could not find admission likelihood data for <span className="rr-empty-panel-strong">{exam}</span>
        {", "}
        <span className="rr-empty-panel-strong">{cat}</span>, and a score of{" "}
        <span className="rr-empty-panel-strong">{formatPercentile(percentile)} percentile</span>. Try
        another percentile, category, or exam, or check back as we add more coverage.
      </p>
    </div>
  );
}

type HapEmptyFilteredProps = { onResetFilters: () => void };

export function HapEmptyFiltered({ onResetFilters }: HapEmptyFilteredProps) {
  return (
    <div className="rr-empty-panel rr-empty-panel--embed" role="status">
      <div className="rr-empty-panel-icon rr-empty-panel-icon--muted" aria-hidden>
        <FunnelSimple size={28} weight="duotone" />
      </div>
      <h3 className="rr-empty-panel-title">Nothing to show for these filters</h3>
      <p className="rr-empty-panel-text">
        Your college filter does not include any rows right now. Open the filter and pick at least one college, or reset to see the full list again.
      </p>
      <button type="button" className="rr-empty-panel-cta" onClick={onResetFilters}>
        Reset filters
      </button>
    </div>
  );
}
